// ============================================================
// THE ARMOURY'S CLAUSES (plan armoury, stage 3) - what is known when an
// attack is declared, beyond the target and the kind of attack:
// a stance of one's own, the attack's toggles, the armed weapon, and
// where it happens (the range band, the distance, the target's cover).
//
// Rows of the same table as `effectWhen`'s own (`ClauseRule`); that module
// composes them. A clause with nothing to judge (no weapon armed, no
// toggles known) is unknown, never false.
//
// Pure. Zero React dependency.
// ============================================================
import { COVER_LABELS, DAMAGE_TYPE_COLORS } from '../constants';
import { oneOf, shortText, type ClauseRule } from './whenParts';

const ACTIONS = {
  aim: { label: 'aiming', test: (a: Readonly<Record<string, boolean>>) => !!(a.aimHalf || a.aimFull) },
  charge: { label: 'charging', test: (a: Readonly<Record<string, boolean>>) => !!a.charge },
  called: { label: 'making a called shot', test: (a: Readonly<Record<string, boolean>>) => !!a.calledShot },
  allOut: { label: 'making an all out attack', test: (a: Readonly<Record<string, boolean>>) => !!a.allOutAttack },
  gangUp: { label: 'ganging up', test: (a: Readonly<Record<string, boolean>>) => !!a.gangUp },
} as const;
type ActionId = keyof typeof ACTIONS;
const ACTION_IDS = Object.keys(ACTIONS) as ActionId[];

const MODES = { single: 'single shot', semi: 'semi auto', full: 'full auto' } as const;
type ModeId = keyof typeof MODES;
const MODE_IDS = Object.keys(MODES) as ModeId[];

const DAMAGE_IDS = Object.keys(DAMAGE_TYPE_COLORS);

/** Nearest first, as the range table runs. */
const BANDS = {
  pointBlank: 'Point Blank',
  short: 'Short',
  normal: 'Normal',
  long: 'Long',
  extreme: 'Extreme',
} as const;
type BandId = keyof typeof BANDS;
const BAND_IDS = Object.keys(BANDS) as BandId[];

/** Where a hit may land, as a clause asks it: an arm or a leg is either side. */
const LANDINGS = {
  head: { label: 'the head', test: (loc: string) => loc === 'head' },
  body: { label: 'the body', test: (loc: string) => loc === 'body' },
  arm: { label: 'an arm', test: (loc: string) => loc === 'rightArm' || loc === 'leftArm' },
  leg: { label: 'a leg', test: (loc: string) => loc === 'rightLeg' || loc === 'leftLeg' },
} as const;
type LandingId = keyof typeof LANDINGS;
const LANDING_IDS = Object.keys(LANDINGS) as LandingId[];
const MAX_ARMOUR = 30;

/** The kinds judged PER HIT: unknown at declaration, settled where each hit lands (`weaponRules.hitRuleBonuses`). */
export const HIT_KINDS: readonly string[] = ['hit_location', 'armour_here'];

/** Least first: "in Half cover" holds behind Three-quarter and Full too. */
const COVER_IDS = ['low', 'half', 'threeQuarter', 'full'] as const;
type CoverId = (typeof COVER_IDS)[number];
const coverAt = (level: string) => (COVER_IDS as readonly string[]).indexOf(level);
const coverWord = (id: CoverId | 'any') =>
  id === 'any' ? 'in cover' : `in ${COVER_LABELS[id].replace(/ \(.*/, '')} cover`;
const MAX_METRES = 1000;

/** A special's name without its value: "Blast(3)" is "blast". */
const specialKey = (rule: string) => rule.replace(/\(.*$/, '').trim().toLowerCase();
const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

export type MoreClause =
  | { kind: 'own_stance'; is: string; not?: true }
  | { kind: 'attack_action'; is: ActionId; not?: true }
  | { kind: 'weapon_special'; is: string; not?: true }
  | { kind: 'weapon_damage'; is: string; not?: true }
  | { kind: 'fire_mode'; is: ModeId; not?: true }
  | { kind: 'weapon_tag'; is: string; not?: true }
  | { kind: 'range_band'; is: BandId; orCloser?: true; not?: true }
  | { kind: 'within'; is: number; not?: true }
  | { kind: 'target_cover'; is: CoverId | 'any'; not?: true }
  | { kind: 'hit_location'; is: LandingId; not?: true }
  | { kind: 'armour_here'; is: number; not?: true };
type MoreKind = MoreClause['kind'];
type Of<K extends MoreKind> = Extract<MoreClause, { kind: K }>;

/** The kinds a SHEET effect may wait on, beside `own_state`: nothing outside oneself (the two tiers). */
export const SELF_KINDS: readonly string[] = ['own_state', 'own_stance'];

export const MORE_CLAUSES: { [K in MoreKind]: ClauseRule<Of<K>> } = {
  own_stance: {
    label: 'I am in the stance',
    blank: { kind: 'own_stance', is: 'stance' },
    read: (raw) => {
      const is = shortText(raw.is, 40);
      return is ? { kind: 'own_stance', is } : null;
    },
    judge: (c, s) => (s.self ? !!s.self.stances?.some((name) => sameName(name, c.is)) : null),
    say: (c) => [`I am in "${c.is}"`, `I am not in "${c.is}"`],
  },
  attack_action: {
    label: 'I am (attacking)',
    blank: { kind: 'attack_action', is: 'aim' },
    options: ACTION_IDS.map((id) => ({ id, label: ACTIONS[id].label })),
    read: (raw) => {
      const is = oneOf(ACTION_IDS, raw.is);
      return is ? { kind: 'attack_action', is } : null;
    },
    judge: (c, s) => (s.action ? ACTIONS[c.is].test(s.action) : null),
    say: (c) => [ACTIONS[c.is].label, `not ${ACTIONS[c.is].label}`],
  },
  weapon_special: {
    label: 'The weapon has the rule',
    blank: { kind: 'weapon_special', is: 'Tearing' },
    read: (raw) => {
      const is = shortText(raw.is, 40);
      return is ? { kind: 'weapon_special', is } : null;
    },
    judge: (c, s) => (s.weapon ? s.weapon.specials.some((rule) => specialKey(rule) === specialKey(c.is)) : null),
    say: (c) => [`the weapon is ${c.is}`, `the weapon is not ${c.is}`],
  },
  weapon_damage: {
    label: 'The weapon deals',
    blank: { kind: 'weapon_damage', is: 'E' },
    options: DAMAGE_IDS.map((id) => ({ id, label: `${id} - ${DAMAGE_TYPE_COLORS[id].label}` })),
    read: (raw) => {
      const is = oneOf(DAMAGE_IDS, raw.is);
      return is ? { kind: 'weapon_damage', is } : null;
    },
    judge: (c, s) => (s.weapon?.damageType ? s.weapon.damageType === c.is : null),
    say: (c) => [`the weapon deals ${c.is} damage`, `the weapon does not deal ${c.is} damage`],
  },
  fire_mode: {
    label: 'Firing',
    blank: { kind: 'fire_mode', is: 'full' },
    options: MODE_IDS.map((id) => ({ id, label: MODES[id] })),
    read: (raw) => {
      const is = oneOf(MODE_IDS, raw.is);
      return is ? { kind: 'fire_mode', is } : null;
    },
    judge: (c, s) => (s.weapon?.mode ? s.weapon.mode === c.is : null),
    say: (c) => [`firing ${MODES[c.is]}`, `not firing ${MODES[c.is]}`],
  },
  weapon_tag: {
    label: 'The weapon is tagged',
    blank: { kind: 'weapon_tag', is: 'tag' },
    read: (raw) => {
      const is = shortText(raw.is, 40);
      return is ? { kind: 'weapon_tag', is } : null;
    },
    judge: (c, s) => (s.weapon ? !!s.weapon.tags?.some((tag) => sameName(tag, c.is)) : null),
    say: (c) => [`the weapon is tagged "${c.is}"`, `the weapon is not tagged "${c.is}"`],
  },
  range_band: {
    label: 'At range',
    blank: { kind: 'range_band', is: 'short', orCloser: true },
    options: BAND_IDS.map((id) => ({ id, label: BANDS[id] })),
    read: (raw) => {
      const is = oneOf(BAND_IDS, raw.is);
      if (!is) return null;
      return raw.orCloser === true ? { kind: 'range_band', is, orCloser: true } : { kind: 'range_band', is };
    },
    judge: (c, s) => {
      const at = s.place?.band ? BAND_IDS.indexOf(s.place.band as BandId) : -1;
      if (at < 0) return null;
      const want = BAND_IDS.indexOf(c.is);
      return c.orCloser ? at <= want : at === want;
    },
    say: (c) => {
      const band = `at ${BANDS[c.is]} range${c.orCloser ? ' or closer' : ''}`;
      return [band, `not ${band}`];
    },
  },
  within: {
    label: 'Within (metres)',
    blank: { kind: 'within', is: 10 },
    read: (raw) => {
      const is = Number(raw.is);
      return Number.isFinite(is) && is > 0 && is <= MAX_METRES ? { kind: 'within', is } : null;
    },
    judge: (c, s) => (typeof s.place?.metres === 'number' ? s.place.metres <= c.is : null),
    say: (c) => [`within ${c.is} m`, `beyond ${c.is} m`],
  },
  target_cover: {
    label: 'The target is',
    blank: { kind: 'target_cover', is: 'any' },
    options: [
      { id: 'any', label: 'in cover' },
      ...COVER_IDS.map((id) => ({ id, label: `${coverWord(id)} or better` })),
    ],
    read: (raw) => {
      const is = oneOf(['any', ...COVER_IDS] as const, raw.is);
      return is ? { kind: 'target_cover', is } : null;
    },
    judge: (c, s) => {
      if (!s.place?.cover) return null;
      return c.is === 'any' ? coverAt(s.place.cover) >= 0 : coverAt(s.place.cover) >= coverAt(c.is);
    },
    say: (c) => {
      const what = c.is === 'any' ? 'in cover' : `${coverWord(c.is)} or better`;
      return [`the target is ${what}`, `the target is not ${what}`];
    },
  },
  hit_location: {
    label: 'It lands on (per hit)',
    blank: { kind: 'hit_location', is: 'head' },
    options: LANDING_IDS.map((id) => ({ id, label: LANDINGS[id].label })),
    read: (raw) => {
      const is = oneOf(LANDING_IDS, raw.is);
      return is ? { kind: 'hit_location', is } : null;
    },
    judge: (c, s) => (s.hit ? LANDINGS[c.is].test(s.hit.location) : null),
    say: (c) => [`it lands on ${LANDINGS[c.is].label}`, `it does not land on ${LANDINGS[c.is].label}`],
  },
  armour_here: {
    label: 'Armour where it lands, at most (per hit)',
    blank: { kind: 'armour_here', is: 4 },
    read: (raw) => {
      const is = Math.trunc(Number(raw.is));
      return Number.isFinite(is) && is >= 0 && is <= MAX_ARMOUR ? { kind: 'armour_here', is } : null;
    },
    judge: (c, s) => (s.hit ? s.hit.armour <= c.is : null),
    say: (c) => [`the armour where it lands is ${c.is} or less`, `the armour where it lands is more than ${c.is}`],
  },
};
